<template>
  <div class="fts-dashboard" ref="dashboard" :data-dashboard-load="loading ? 'loading' : 'loaded'" @scroll="onScroll">
    <div class="fts-client-header" ref="clientHeader">
      <div class="client-header-wrapper">
        <div class="client-header__l">
          <div class="client-header-label">
            <font-awesome-icon :icon="['fas', 'chart-column']" size="lg" class="client-header-label-icon" />
            <div class="client-header-label-title">
              Dashboard
            </div>
          </div>
        </div>
        <div class="client-header__r">
          <!-- filter -->
          <fts-tippy :content="`${isFilterEnabled ? 'Disable' : 'Enable'} filter (alpha)`" class="client-header-action"
            v-if="isInternal">
            <fts-btn icon text :color="isFilterEnabled ? 'primary' : 'default'" :disabled="loading"
              @click="setFilter(!isFilterEnabled)">
              <font-awesome-icon :icon="['fas', 'filter']" />
            </fts-btn>
          </fts-tippy>
          <!-- add widget -->
          <fts-tippy content="Add widget" class="client-header-action" v-if="isConfigurable">
            <fts-btn slot="trigger" icon text @click="createWidget">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </fts-btn>
          </fts-tippy>
          <!-- refresh -->
          <fts-tippy content="Refresh" class="client-header-action">
            <fts-btn slot="trigger" icon text :loading="loading" @click="refresh">
              <font-awesome-icon :icon="['fas', 'sync-alt']" />
            </fts-btn>
          </fts-tippy>
          <!-- saving -->
          <div class="client-header-save client-header-action" v-if="autoSaving">Saving...</div>
        </div>
      </div>
    </div>
    <!-- The analytics family home (analytic proposal ruling 8 / §12d): Dashboards (this page, the
         untouched Overview board) · Reports (P2) · My Insights (GNey one-offs). Internal-first. -->
    <div v-if="isInternal" class="dashboard-tabs">
      <button type="button" class="dashboard-tab" :class="{ 'is-on': tab === 'dashboards' }" @click="tab = 'dashboards'">Dashboards</button>
      <button type="button" class="dashboard-tab" :class="{ 'is-on': tab === 'reports' }" @click="tab = 'reports'">Reports</button>
      <button type="button" class="dashboard-tab" :class="{ 'is-on': tab === 'insights' }" @click="tab = 'insights'">My Insights <span class="dashboard-tab-alpha">alpha</span></button>
      <!-- Names the surface AND opens it — in this page, never as a second panel (user ruling
           2026-08-23). These tabs ARE the Analytic Bench; landing on one puts GNey in analytics
           mode (§4g). -->
      <button type="button" class="dashboard-bench-label" title="Open the Analytic Bench — GNey's analytics surface, here in this page" @click="tab = 'insights'">
        <font-awesome-icon :icon="['fas', 'chart-column']" /> <span>Analytic Bench</span>
      </button>
    </div>
    <!-- Reports · My Insights: the Analytic Bench, in this page (user ruling 2026-08-23 — no second
         rail, no popup). The tab row above is its navigation; the Dashboards tab is the native
         Overview board below. Mounting it is what puts GNey in analytics mode. -->
    <div v-if="tab !== 'dashboards'" class="dashboard-analytics-pane">
      <fts-analytic-bench :section="tab" :report-id="routeReportId" :analytic-id="routeAnalyticId" />
    </div>
    <!-- dashboard widget -->
    <div class="dashboard-widget-container" v-show="tab === 'dashboards'">
      <div class="dashboard-greeting">
        <div class="dashboard-greeting__l">
          <div class="dashboard-greeting-title">
            Welcome,
            <span class="fts-cursor-pointer" @click="openUserProfileModal">
              {{ userProfileName }}
            </span>
          </div>
        </div>
      </div>
      <!-- approval requests waiting for this person -->
      <fts-approval-waiting-list />

      <!-- filter -->
      <div class="dashboard-filter" v-if="isFilterEnabled">
        <div class="dashboard-filter-control">

          <div class="dashboard-filter-module">
            <fts-form-element :form-data="filters" :item="{
              type: 27,
              displayName: 'Module',
              fieldName: 'module',
              label: false
            }" comfort-design @input="updateFilter" key="moduleId">
              <fts-select slot="formElement" slot-scope="{ formData, item, onInput, onBlur, error }"
                v-model="formData.moduleId" :options="workspaceModules" placeholder="Module" comfort-design
                popper-prevent-overflow-alt-axis @input="onInput" @search:blur="onBlur">
                <fts-label-group slot="selected-option-container" slot-scope="{ option }" :item-id="option.id"
                  :list="workspaceModules" emoji />
                <fts-label-group slot="option" slot-scope="{ option }" :item-id="option.id" :list="workspaceModules"
                  emoji />
              </fts-select>
            </fts-form-element>
          </div>

          <fts-tippy content="Disable filter">
            <fts-btn text icon @click="setFilter(false)">
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </fts-btn>
          </fts-tippy>
        </div>

        <div class="dashboard-filter-query" v-if="filters.moduleId">
          <fts-query-designer :modules="workspaceModules" :states="workspaceStates" :information-source="queryColumns"
            :filter="filters" :mode="'view'" />
        </div>
      </div>
      <!-- shortcut -->
      <fts-dashboard-widget :selectedModuleId="filters.moduleId" :modules="workspaceModules" :states="workspaceStates"
        :dashboard-data="dashboardData" :filters="{
          moduleId: this.filters.moduleId,
          filters: this.filters
        }" :loading="loading" v-model="shortcutWidgets" draggable @configure="x => configure(x.formData, x.callback)"
        @entry:create="entryCreate" @entry:view="entryView" @route:listing="routeListing" @update="updateLayout"
        @delete="deleteWidget" v-if="Boolean(shortcutWidgets && shortcutWidgets.length)" />
      <!-- chart -->
      <fts-dashboard-widget :selectedModuleId="filters.moduleId" :modules="workspaceModules" :states="workspaceStates"
        :dashboard-data="dashboardData" :filters="{
          moduleId: this.filters.moduleId,
          filters: this.filters
        }" :loading="loading" v-model="workspaceWidgets" draggable @configure="x => configure(x.formData, x.callback)"
        @entry:create="entryCreate" @entry:view="entryView" @route:listing="routeListing" @update="updateLayout"
        @delete="deleteWidget" @segment-clicked="segmentClicked"
        v-if="Boolean(workspaceWidgets && workspaceWidgets.length)" />
      <!-- predefined card -->
      <fts-dashboard-widget :modules="workspaceModules" :states="workspaceStates" :dashboard-data="dashboardData"
        :loading="loading" :initial="initial" :activity-everyone-allowed="activityEveryoneAllowed"
        v-model="predefinedCards" draggable @configure="configure"
        @entry:create="entryCreate" @entry:view="entryView" @route:listing="routeListing" @update="updateLayout"
        @delete="deleteWidget" v-if="Boolean(predefinedCards && predefinedCards.length)" />
    </div>
    <!-- widget design modal -->
    <fts-widget-design-modal ref="ftsWidgetDesignModal" />
    <!-- widget chart design modal -->
    <fts-widget-chart-design-modal :modules="accessibleWorkspaceModules" :list="workspaceWidgets"
      ref="ftsWidgetChartDesignModal" />
    <!-- widget shortcut design modal -->
    <fts-widget-shortcut-design-modal :modules="accessibleWorkspaceModules" :list="shortcutWidgets"
      ref="ftsWidgetShortcutDesignModal" />
    <!-- widget predefined design modal -->
    <fts-widget-predefined-design-modal ref="ftsWidgetPredefinedDesignModal" />
    <!-- user profile -->
    <fts-user-profile client ref="ftsUserProfile" />
  </div>
</template>

<script>
import Vue from "vue";

import commons from "../commons/commons";
import FTSApprovalWaitingList from "../approvals/FTSApprovalWaitingList";
import dashboardWidget from "../commons/dashboardWidget";
import { authorizationStore } from "../store/authorizationStore";
import { clientStore } from "../store/clientStore";
import { store as canvasDesignStore } from '../store/canvasDesignStore';


import VueScrollTo from "vue-scrollto";
Vue.use(VueScrollTo);

import FTSWidgetDesignModal from "../dashboard/design/FTSWidgetDesignModal";
import FTSWidgetChartDesignModal from "../dashboard/design/FTSWidgetChartDesignModal";
import FTSWidgetShortcutDesignModal from "../dashboard/design/FTSWidgetShortcutDesignModal";
import FTSWidgetPredefinedDesignModal from "../dashboard/design/FTSWidgetPredefinedDesignModal";
import FTSDashboardWidget from "../dashboard/FTSDashboardWidget";
import FTSBtn from "../elements/FTSBtn";
import FTSTippy from "../elements/FTSTippy";
import FTSUserProfile from "../elements/FTSUserProfile";
import FTSQueryDesigner from "../design/FTSQueryDesigner";
import FTSSelect from '../elements/FTSSelect';
import FTSFormElement from '../elements/FTSFormElement';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSAnalyticBench from '../elements/FTSAnalyticBench';

import queryLogic from "../commons/queryLogic";

export default {
  components: {
    "fts-approval-waiting-list": FTSApprovalWaitingList,
    "fts-widget-design-modal": FTSWidgetDesignModal,
    "fts-widget-chart-design-modal": FTSWidgetChartDesignModal,
    "fts-widget-shortcut-design-modal": FTSWidgetShortcutDesignModal,
    "fts-widget-predefined-design-modal": FTSWidgetPredefinedDesignModal,
    "fts-dashboard-widget": FTSDashboardWidget,
    "fts-btn": FTSBtn,
    "fts-tippy": FTSTippy,
    "fts-user-profile": FTSUserProfile,
    "fts-query-designer": FTSQueryDesigner,
    "fts-select": FTSSelect,
    "fts-form-element": FTSFormElement,
    "fts-label-group": FTSLabelGroup,
    "fts-analytic-bench": FTSAnalyticBench,
  },
  data: () => ({
    dashboardData: {},
    loading: false,
    initial: false,
    autoSaving: false,
    activityEveryone: false,
    /* SS03547: false when the workspace restricts the stream's "everyone" mode to admins and
       this user is not one - the me/everyone switch is hidden and the server serves "me". */
    activityEveryoneAllowed: true,
    interval: null,
    resizeObserver: null,
    isFilterEnabled: false,
    isFilterModuleId: null,
    filters: { moduleId: null, items: [] },
    queryColumns: [],
    // Analytics family tabs (ruling 8): dashboards | reports | insights.
    tab: "dashboards",
  }),
  methods: {
    // Analytics family tabs (ruling 8): honour ?tab=dashboards|reports|insights — a chat report
    // card routes here. Only while the tab row exists (internal-first), else the board stays.
    applyRouteTab() {
      const q = (this.$route && this.$route.query) || {};
      if (!this.isInternal) return;
      if (["dashboards", "reports", "insights"].includes(q.tab)) this.tab = q.tab;
      else if (q.report) this.tab = "insights"; // ?report= alone still means My Insights
      else if (q.analytic) this.tab = "reports"; // ?analytic= alone means the Reports library
    },
    async updateFilter() {
      if (this.filters.moduleId) {
        if (this.filters.moduleId == this.previousModuleId) {
          return;
        }

        this.isFilterModuleId = this.filters.moduleId;
        this.filters.items = [];

        try {
          const res = await canvasDesignStore.dispatch("loadModule", this.filters.moduleId);
          if (res && res.data) {
            this.queryColumns = res.data.information;

            /* query selection suffix */
            this.queryColumns = this.queryColumns.concat(
              queryLogic.querySelectionSuffix
                .filter(x => x.fieldName === 'and' || x.fieldName === 'or')
                .map(y => ({ ...y, system: true }))
            );

            this.queryColumns = this.queryColumns.concat([
              {
                id: 'documentId',
                displayName: "ID",
                system: true,
                fieldName: "documentId",
                type: 0
              },
              {
                id: 'state',
                displayName: "State",
                system: true,
                fieldName: "state",
                type: "state"
              },
            ]);
          }
        } catch (err) {
          console.error(err);
          this.$toasted.global.error({ response: err.response });
        }
      } else {
        this.queryColumns = [];
        this.isFilterModuleId = null;
        this.filters = { moduleId: null, items: [] };
      }
    },
    setFilter(val) {
      if (val === true) {
        this.isFilterEnabled = true;
      } else {
        // reset filter
        this.isFilterEnabled = false;
        this.isFilterModuleId = null;
        this.filters = { moduleId: null, items: [] };
      }
    },
    async segmentClicked(payload) {
      // alert(payload);
      console.log(payload);

      if (!this.isFilterEnabled)
        this.isFilterEnabled = true;

      if (this.filters == null || this.filters.moduleId != payload.item.moduleId) {
        this.filters = { moduleId: payload.item.moduleId, items: [] };
        await this.updateFilter();
      }

      if (payload.item.groupByInformationId == 'state') {
        let state = this.workspaceStates.find(x => x.module == payload.item.moduleId && x.name == payload.payload.label);
        if (state) {
          this.filters.items.push({
            fieldId: 'state',
            fieldName: 'state',
            operator: 'is',
            payload: state
          })
        }

      } else {
        let field = this.queryColumns.find(x => x.id == payload.item.groupByInformationId);

        if (field) {
          /* SS05396: a chart grouped by a user field renders the assignee's display name, but
             the filter matches on the username - resolve the label back before drilling down. */
          const userInformationIds = ['assignTo', 'createdBy', 'lastChangeBy', 'username'];
          let value = payload.payload.label;

          if (userInformationIds.indexOf(field.id) > -1) {
            const user = this.workspaceUsers
              .find(x => x.displayName && x.displayName.trim() === value);

            if (user) {
              value = user.username;
            }
          }

          this.filters.items.push({
            fieldId: field.id,
            fieldName: field.fieldName,
            operator: 'is',
            payload: value
          })
        }
      }









      // this.filters = { items: [{id: "JNM59JlI62bR", fieldId: "nBSmbc", fieldName: "lrMbym", operator: "contains", payload: "IT"}] }
    },
    toggleActivityStreamModeHandler(isEveryone) {
      if (isEveryone) {
        this.activityEveryone = true;
        this.initDashboard();
      } else {
        this.activityEveryone = false;
        this.initDashboard();
      }
    },
    onScroll(evt) {
      try {
        const isScroll = evt.target.scrollTop > 0;
        if (isScroll)
          this.$refs.clientHeader.classList.add('fts-client-header--scroll');
        else if (!isScroll)
          this.$refs.clientHeader.classList.remove('fts-client-header--scroll');
      } catch (err) {
        console.warn("[DOMException]", err);
      }
    },
    async saveLayout() {
      try {
        this.autoSaving = true;
        await clientStore.dispatch('updateDashboardWidget');
      } catch (err) {
        console.error(err);
        this.$toasted.global.error({ response: err.response });
      } finally {
        this.autoSaving = false;
      }
    },
    updateLayout: _.debounce(async function () {
      await this.saveLayout();
    }, 3000),
    async initDashboard() {
      this.loading = true;
      try {
        let res = await clientStore.dispatch('retrieveDashboard', {
          activityEveryone: this.activityEveryone,
        });

        this.dashboardData = {
          myTasks: res.data.myTasks || [],
          mentionedMe: res.data.mentionedMe || [],
          initiatedByMe: res.data.initiatedByMe || [],
          myWatchList: res.data.myWatchList || [],
          myActivities: res.data.myActivities || [],
          dues: res.data.dues || [],
          upcoming: res.data.upcoming || [],
          happeningNow: res.data.happeningNow || [],
        }
        // SS03547: the server says whether this caller may switch the stream to "everyone" and
        // serves "me" regardless when not - mirror that so the switch never shows "everyone"
        // highlighted over the user's own activity. An older server (no field) allows.
        this.activityEveryoneAllowed = !(res.data.activityEveryoneAllowed === false);
        if (!this.activityEveryoneAllowed) this.activityEveryone = false;
        // temp
        this.dashboardData.myTasks = _.orderBy(this.dashboardData.myTasks, x => x.audit.updatedDate, ['desc']);
      } catch (err) {
        this.dashboardData = {};
        if (err.response && err.response.data && err.response.data.message)
          console.error(err.response.data.message);
        else
          console.error("unable load dashboard");
      }
      this.loading = false;
      this.initial = true;
    },
    refresh() {
      this.initDashboard();
      /* shortcut */
      if (this.shortcutWidgets && this.shortcutWidgets.length) {
        window.eventBus.$emit("refreshShortcutWidget");
      }
      /* chart */
      if (this.workspaceWidgets && this.workspaceWidgets.length) {
        window.eventBus.$emit("refreshChartWidget");
      }
    },
    routeListing(item) {
      const route = commons.clientListViewRoute(item);
      this.$router.push(route);
    },
    entryCreate(moduleId) {
      window.eventBus.$emit("quickViewData", {
        activityId: "create",
        vectorId: moduleId,
      });
    },
    entryView(item) {
      window.eventBus.$emit("quickViewData", {
        id: item.id,
        vectorId: item.vectorId,
      });
    },
    async createWidget() {
      const res = await this.$refs.ftsWidgetDesignModal.show();
      setTimeout(() => {
        if (res === 'shortcut') {
          this.openShortcutDesign();
        } else if (res === 'chart') {
          this.openChartDesign();
        } else if (res === 'predefined') {
          this.openPredefinedDesign();
        }
      }, 500);
    },
    configure(formData, callback) {
      if (formData.widgetType === 'shortcut') {
        this.openShortcutDesign(formData, callback);
      } else if (formData.widgetType === 'chart') {
        this.openChartDesign(formData, callback);
      }
    },
    scrollToWidget(dataId) {
      try {
        const el = document.querySelector(`[data-id="${dataId}"]`);
        const removeStyleProperty = () => {
          el.style.removeProperty('box-shadow');
          el.removeEventListener('transitionend', removeStyleProperty);
          setTimeout(() => {
            el.style.removeProperty('transition');
          }, 1500);
        }
        if (el) {
          VueScrollTo.scrollTo(el, 500, {
            container: this.$refs.dashboard,
            offset: -80,
            onDone: () => {
              el.style.transition = "all 1.5s ease-in-out";
              el.style.boxShadow = "0px 0px 10px 5px rgba(43,158,198,0.25)";
              el.addEventListener('transitionend', removeStyleProperty);
            }
          });
        }
      } catch (err) {
        console.warn("[DOMException]", err);
      }
    },
    async deleteWidget(item) {
      const getSubmitData = (list, widgetId, errorMessage) => {
        let widgets = [...list];
        const widgetIndex = widgets.findIndex(x => x.id == widgetId);
        if (widgetIndex > -1)
          widgets.splice(widgetIndex, 1);
        else if (errorMessage)
          this.$toasted.error(errorMessage);

        return widgets;
      }

      try {
        if (item.widgetType == 'shortcut') {
          const list = getSubmitData(
            this.shortcutWidgets,
            item.id,
            "Failed to delete shortcut."
          );
          await clientStore.dispatch('updateDashboardWidget', { shortcuts: list });
          this.shortcutWidgets = list;
          this.$toasted.success(`Shortcut deleted.`);
        } else if (item.widgetType == 'chart') {
          const list = getSubmitData(
            this.workspaceWidgets,
            item.id,
            "Failed to delete chart."
          );
          await clientStore.dispatch('updateDashboardWidget', { widgets: list });
          this.workspaceWidgets = list;
          this.$toasted.success(`Chart deleted.`);
        } else if (item.widgetType == 'predefined') {
          const list = getSubmitData(
            this.predefinedCards,
            item.id,
            `Failed to remove predefined card, ${item.name}.`
          );
          await clientStore.dispatch('updateDashboardWidget', { dashboard: list });
          this.predefinedCards = list;
          this.$toasted.success(`Predefined Card, ${item.name} removed.`);
        } else {
          this.$toasted.error("Failed to delete. Undefined widget.");
        }
      } catch (err) {
        this.$toasted.global.error({ response: err.response });
      }
    },
    async openShortcutDesign(formData, callback) {
      formData = { ...formData };
      if (!formData.id) {
        formData.id = commons.nanoID();
      }
      let list = [...this.shortcutWidgets];
      const res = await this.$refs.ftsWidgetShortcutDesignModal.show(formData);
      if (Boolean(res) && res !== true) {
        try {
          const findIndex = list.findIndex(x => x.id === res.id);
          if (findIndex > -1) {
            this.$set(list, findIndex, res);
          } else {
            list.push(res);
          }
          await clientStore.dispatch('updateDashboardWidget', { shortcuts: list });
          /* set $store */
          this.shortcutWidgets = list;
          /* toasted */
          if (findIndex > -1) {
            this.$toasted.success("Widget updated.");
          } else {
            this.$toasted.success("Widget added.");
            this.$nextTick(() => { this.scrollToWidget(formData.id) });
          }
          /* close modal */
          this.$refs.ftsWidgetShortcutDesignModal.reset();
          this.$nextTick(async () => {
            /* callback refresh */
            if (callback && typeof callback.refresh === 'function') {
              await callback.refresh();
            }
          });
        } catch (err) {
          this.$refs.ftsWidgetShortcutDesignModal.reset();
          this.$toasted.global.error({ response: err.response });
        }
      } else if (res === true) {
        this.deleteWidget(formData);
      }
    },
    async openChartDesign(formData, callback) {
      formData = { ...formData };
      if (!formData.id) {
        formData.id = commons.nanoID();
      }
      let list = [...this.workspaceWidgets];
      const res = await this.$refs.ftsWidgetChartDesignModal.show(formData);
      if (Boolean(res) && res !== true) {
        try {
          const findIndex = list.findIndex(x => x.id === res.id);
          if (findIndex > -1) {
            this.$set(list, findIndex, res);
          } else {
            list.push(res);
          }
          await clientStore.dispatch('updateDashboardWidget', { widgets: list });
          /* set $store */
          this.workspaceWidgets = list;
          /* toasted */
          if (findIndex > -1) {
            this.$toasted.success("Widget updated.");
          } else {
            this.$toasted.success("Widget added");
            this.$nextTick(() => { this.scrollToWidget(formData.id) });
          }
          /* close modal */
          this.$refs.ftsWidgetChartDesignModal.reset();
          this.$nextTick(async () => {
            /* callback refresh */
            if (callback && typeof callback.refresh === 'function') {
              await callback.refresh();
            }
          });
        } catch (err) {
          this.$refs.ftsWidgetChartDesignModal.reset();
          this.$toasted.global.error({ response: err.response });
        }
      } else if (res === true) {
        this.deleteWidget(formData);
      }
    },
    async openPredefinedDesign() {
      const res = await this.$refs.ftsWidgetPredefinedDesignModal.show(this.predefinedCards);
      if (res && res != true) {
        let result = [];
        if (res.length) {
          result = this.predefinedCards.filter(x => res.some(y => y.id == x.id));
          const predefinedCards = dashboardWidget.predefinedCards.filter(x =>
            !result.some(y => y.id == x.id) && res.some(y => y.id == x.id)
          );
          if (predefinedCards.length) {
            result = result.concat(predefinedCards);
          }
        }
        this.predefinedCards = result;
        this.saveLayout();
      }
    },
    openUserProfileModal() {
      this.$refs.ftsUserProfile.show();
    }
  },
  computed: {
    filterColumns() {
      return [
        {
          "id": "documentId",
          "system": true,
          "displayName": "ID",
          "fieldName": "documentId",
          "type": 0
        },
        {
          "id": "state",
          "system": true,
          "displayName": "State",
          "fieldName": "state",
          "type": "state"
        },
        {
          "id": "date",
          "system": true,
          "displayName": "Date",
          "fieldName": "date",
          "type": 5
        },
        {
          "id": "createdBy",
          "system": true,
          "displayName": "Created By",
          "fieldName": "createdBy",
          "type": 20
        },
        {
          "id": "lastChangeBy",
          "system": true,
          "displayName": "Last Change By",
          "fieldName": "lastChangeBy",
          "type": 20
        }
      ];
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    // ?report=<id> — the insight a chat report card asked for.
    routeReportId() {
      const q = (this.$route && this.$route.query) || {};
      return q.report ? String(q.report) : "";
    },
    // ?analytic=<id> — a PUBLISHED report (Reports tab), e.g. from the chat's publish receipt.
    routeAnalyticId() {
      const q = (this.$route && this.$route.query) || {};
      return q.analytic ? String(q.analytic) : "";
    },
    isConfigurable() {
      return authorizationStore.getters.isAdminUser;
    },
    userProfileName() {
      return authorizationStore.getters.userProfile.displayName;
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceStates() {
      return clientStore.getters.workspaceStates;
    },
    workspaceUsers() {
      return clientStore.getters.workspaceUsers;
    },
    accessibleWorkspaceModules() {
      return clientStore.getters.workspaceMenus && commons.getModulesFromWorkspaceMenus(clientStore.getters.workspaceMenus);
    },
    shortcutWidgets: {
      get() {
        return clientStore.getters.workspaceShortcuts || [];
      },
      set(val) {
        clientStore.dispatch("setWorkspaceWidget", { shortcuts: val });
      }
    },
    workspaceWidgets: {
      get() {
        return clientStore.getters.workspaceWidgets || [];
      },
      set(val) {
        clientStore.dispatch("setWorkspaceWidget", { widgets: val });
      }
    },
    predefinedCards: {
      get() {
        if (clientStore.getters.workspaceDashboard) {
          return clientStore.getters.workspaceDashboard.map(x => {
            const widget = dashboardWidget.predefinedCards.find(y => y.id == x.id);
            return widget ? { ...widget, row: x.row || widget.row } : x;
          }).filter(x => x.id !== 'myWatch');
        }
        return dashboardWidget.predefinedCards;
      },
      set(val) {
        clientStore.dispatch("setWorkspaceWidget", { dashboard: val });
      }
    }
  },
  watch: {
    filters: {
      deep: true,
      handler: _.debounce(async function (v) {
        this.refresh();
      }, 1000),
    },
    workspaceId() {
      this.initial = false;
      this.initDashboard();
    },
    // A chat report card lands here via ?tab=/?report= (same route, query only).
    "$route.query.tab"() {
      this.applyRouteTab();
    },
    "$route.query.report"() {
      this.applyRouteTab();
    },
    "$route.query.analytic"() {
      this.applyRouteTab();
    },
  },
  async mounted() {
    this.applyRouteTab();
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(_.debounce(() => {
        window.requestAnimationFrame(() => {
          try {
            window.eventBus.$emit("dashboard:resize");
          } catch (err) {
            console.warn("ResizeObserver", err);
          }
        });
      }, 100));

      this.resizeObserver.observe(this.$el);
    }

    if (this.$route.query.id) {
      this.$nextTick(() => {
        const queryDecodeids = commons.queryDecodeids(this.$route.query.id);
        if (queryDecodeids.entryId) {
          window.eventBus.$emit("quickViewData", {
            id: queryDecodeids.entryId,
            vectorId: queryDecodeids.moduleId,
            activityId: this.$route.query.a,
          });
        }
      });
    }

    await this.initDashboard();
    // remove when activity stream API ready
    window.eventBus.$on("toggleActivityStreamMode", this.toggleActivityStreamModeHandler);
    // end
    window.eventBus.$on("dashboardRefresh", this.refresh);

    this.interval = setInterval(() => {
      window.eventBus.$emit("refreshChartWidgetLastUpdated");
    }, 60000);
  },
  beforeDestroy() {
    window.eventBus.$off("toggleActivityStreamMode", this.toggleActivityStreamModeHandler);
    window.eventBus.$off("dashboardRefresh", this.refresh);

    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$el);
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    clearInterval(this.interval);
    this.interval = null;
  }
}
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars.scss" as *;

/* analytics family tabs + the My Insights / Reports panes (ruling 8) */
.dashboard-tabs { display: flex; align-items: center; gap: 4px; padding: 8px 24px 0; }
.dashboard-tab {
  border: none; background: transparent; cursor: pointer; padding: 6px 12px; border-radius: 999px;
  font-size: 13px; color: var(--tm-text-muted, #6b7280);
}
.dashboard-tab.is-on { background: rgba(79, 70, 229, 0.1); color: var(--tm-accent, #4f46e5); font-weight: 600; }
.dashboard-tab-alpha {
  display: inline-block; margin-left: 4px; padding: 0 5px; border-radius: 999px; vertical-align: middle;
  font-size: 9px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--tm-accent, #4f46e5); background: rgba(79, 70, 229, 0.12);
}
/* The surface's name AND the way into it, beside the tabs it names (kept adjacent, never pushed to
   the far edge of a wide page). Reads as a quiet link until hover, so it never competes with the
   tabs — but it is a real control. */
.dashboard-bench-label {
  display: inline-flex; align-items: center; gap: 6px; margin-left: 10px; padding: 6px 12px 6px 14px;
  border: none; border-left: 1px solid rgba(0, 0, 0, 0.12); border-radius: 0 999px 999px 0;
  background: transparent; cursor: pointer;
  font-size: 12.5px; font-weight: 600; color: var(--tm-text-muted, #6b7280); white-space: nowrap;
}
.dashboard-bench-label:hover { background: rgba(79, 70, 229, 0.08); color: var(--tm-accent, #4f46e5); }
.dashboard-analytics-pane { padding: 12px 24px; }

$d-min-width: 450px;

.fts-dashboard {
  height: 100%;
  min-width: $d-min-width;
  overflow: auto;
  background-color: var(--fts-background);

  .dashboard-greeting {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    border-radius: 0.5rem;

    .dashboard-greeting__l {
      flex: 1;
      margin-right: 1rem;
      overflow-wrap: break-word;
    }

    .dashboard-greeting-title {
      font-weight: 500;
      font-size: 1.125rem;
      line-height: 1.5rem;
      color: var(--fts-gray-700);
      cursor: default;
    }
  }

  .dashboard-widget-container {
    margin: 0 1rem 1rem;
  }

  .client-header-save {
    font-size: 0.875rem;
    color: var(--fts-default);
    cursor: default;
  }
}
</style>

<style lang="scss">
.fts-dashboard {
  .dashboard-filter {
    margin-bottom: 2rem;
    padding-top: 0.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--fts-border);

    .dashboard-filter-control {
      display: flex;
      align-items: center;

      .dashboard-filter-module {
        flex: 1 0 576px;
        max-width: 576px;
        margin-right: 1rem;

        .fts-form-group {
          margin-bottom: 0;
        }
      }
    }

    .dashboard-filter-query {
      margin-top: 0.75rem;
    }
  }
}


/* --- Calm density, style-only round (docs/frontend-dashboard-calm-density-proposal.md
   rev 2, rulings S0-S15). Everything below is CSS: no template, prop, copy or data is
   touched on this page. Namespaced on the Client shell root (Client.vue:3) intersected
   with this page's root, so the sticky header keeps its look on Calendar and Lobby, and
   the Studio / Builder designer dashboard (same .fts-dashboard class, no --refined
   ancestor, a different frame component) and the mobile chart page are untouched.
   Vue 2 puts the scoping attribute on a rule's last selector, so the scoped rules in
   play top out at (0,3,0) and this namespace outweighs all of them - nothing here needs
   !important. Every colour is an existing theme token, so dark mode follows. The header, the ghost
   icon button and the --cd-* tokens are shared with Calendar and Lobby and live in
   Client.vue; this block is what belongs to the dashboard alone. */
.fts-client.fts-client--refined .fts-dashboard {
  /* S2 - analytics tabs (internal): text weight and an ink underline; no indigo left */
  .dashboard-tabs {
    gap: 2px;
    padding: 4px 16px 0;
  }
  .dashboard-tab {
    padding: 5px 8px;
    border-radius: 4px;
    font-size: 13px;
    color: var(--cd-ink-2);
    &:hover {
      background: var(--cd-hover);
      color: var(--fts-text);
    }
    &.is-on {
      border-radius: 0;
      background: transparent;
      box-shadow: inset 0 -2px 0 var(--fts-text);
      color: var(--fts-text);
      font-weight: 600;
    }
  }
  .dashboard-tab-alpha {
    margin-left: 4px;
    padding: 0;
    background: transparent;
    color: var(--cd-ink-2);
    font-weight: 600;
    font-size: 10px;
  }
  .dashboard-bench-label {
    margin-left: 6px;
    padding: 5px 8px;
    border-left: 0;
    border-radius: 4px;
    color: var(--cd-ink-2);
    font-weight: 500;
    font-size: 12px;
    &:hover {
      background: var(--cd-hover);
      color: var(--fts-text);
    }
  }
  .dashboard-analytics-pane {
    padding: 12px 16px;
  }

  /* S3 - greeting: 48px of headline becomes one 28px line; the name stays clickable */
  .dashboard-greeting {
    margin-bottom: 0.5rem;
    padding-top: 0;
    padding-bottom: 0;
    .dashboard-greeting-title {
      font-weight: 500;
      font-size: 0.8125rem;
      line-height: 1.75rem;
      color: var(--cd-ink-2);
      .fts-cursor-pointer:hover {
        color: var(--fts-text);
        text-decoration: underline;
      }
    }
  }

  /* S4 - filter block (internal alpha) */
  .dashboard-filter {
    margin-bottom: 1rem;
    padding-top: 0.25rem;
    padding-bottom: 0.75rem;
    border-bottom-color: var(--cd-hairline);
    .dashboard-filter-query {
      margin-top: 0.5rem;
    }
  }

  /* S5 - grid: 16px gaps and a 128px unit become 12px and 120px
     (n x 120 + (n-1) x 12 = 120 / 252 / 384 / 516 / 648 / 780) */
  .fts-dashboard-widget {
    column-gap: 0.75rem;
    .dashboard-widget-wrapper:not(.dashboard-widget-col-full) {
      margin-bottom: 0.75rem;
    }
    /* a shortcut holds a two-line header and one value:
       1 + 12 + 38 + 8 + 24 + 18 + 12 + 1 = 114px, down from 120. The header is 38, not 34 -
       FTSMoji sizes the title's emoji inline at 20px, so that line is 20 tall whatever the
       label's line-height - and the panel's hairline (S6) counts too. At 104 the tile was 6px
       overfull: the header was squeezed and its module line ran into the count (SS06122).
       The spans keep the 120px unit - their panels hold lists and plots. */
    .dashboard-widget-row {
      height: 114px;
      max-height: 114px;
    }
    .dashboard-widget-row-2 {
      height: 252px;
      max-height: 252px;
    }
    .dashboard-widget-row-3 {
      height: 384px;
      max-height: 384px;
    }
    .dashboard-widget-row-4 {
      height: 516px;
      max-height: 516px;
    }
    .dashboard-widget-row-5 {
      height: 648px;
      max-height: 648px;
    }
    .dashboard-widget-row-6 {
      height: 780px;
      max-height: 780px;
    }
  }

  /* S6 - a widget is a panel: one hairline on the page ground, no shadow. The four
     pastel grounds are class-level defaults (widget.color, which would beat them, is
     inline but never written by any designer path), so this needs no !important and a
     colour an admin does choose still wins. */
  .d-widget-shortcut,
  .d-widget-chart.d-widget-chart--card,
  .d-widget-list-card {
    border: 1px solid var(--cd-hairline);
    border-radius: 0.375rem;
    box-shadow: none;
    background-color: var(--fts-card-background);
  }
  .d-widget-shortcut,
  .d-widget-chart.d-widget-chart--card {
    padding: 0.75rem;
  }
  .d-widget-list-card {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    overflow: hidden;
  }

  /* S7 - shortcut tiles: FTSWidgetTitle keeps its own stacked layout - the name, then the
     module under it, both left-aligned - and only its type quiets. A one-line row was tried
     and backed out: the wrapper is FTSTippy, whose trigger is a block div, so a separator
     drawn before it breaks the line and leaves the module hanging under a stray dot. */
  .fts-widget-title {
    .d-widget-title-label {
      font-weight: 600;
      font-size: 0.8125rem;
      line-height: 1rem;
    }
    .d-widget-title-sub {
      margin-top: 0.125rem;
      .d-widget-title-label {
        font-weight: 400;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--cd-ink-2);
      }
    }
  }
  .d-widget-shortcut {
    /* the 96px watermark is decoration - the title already carries the emoji */
    .d-widget-shortcut-overlay {
      display: none;
    }
    /* the header is never the part that gives: squeezed, its second line spills onto the count */
    .d-widget-shortcut-header {
      flex-shrink: 0;
      height: auto;
      min-height: 1.25rem;
    }
    .d-widget-shortcut-footer {
      margin-top: 0.5rem;
    }
    .d-widget-shortcut-count-header {
      margin-bottom: 0;
    }
    .d-widget-shortcut-count-value {
      font-weight: 600;
      font-size: 1.5rem;
      line-height: 1.5rem;
      font-variant-numeric: tabular-nums;
    }
    .d-widget-shortcut-count-footer {
      margin-top: 0.125rem;
    }
    .d-widget-shortcut-aggregate,
    .d-widget-shortcut-information {
      font-size: 0.75rem;
      line-height: 1rem;
      color: var(--cd-ink-2);
    }
    .d-widget-shortcut-information::before {
      content: "\00b7";
      margin-right: 0.25rem;
      color: var(--fts-gray-300);
    }
    /* New is the page's one accent: the button is class-driven (color="primary"
       resolves to fts-btn-primary, no inline colour), so a text button fills in CSS */
    .d-widget-shortcut-new .fts-btn.fts-btn-v2.fts-btn-text.fts-btn-primary {
      min-height: 1.75rem;
      padding: 0 0.625rem;
      border-radius: 0.25rem;
      background-color: var(--fts-primary);
      color: var(--fts-light);
      font-size: 0.75rem;
      letter-spacing: 0;
      svg {
        color: var(--fts-light);
      }
    }
  }

  /* S8 - chart panels: the footer's three stacked blocks become one 24px line. The
     per-chart timestamp stays; this round has no facts strip to carry it. */
  .d-widget-chart {
    .d-widget-chart-header {
      margin-bottom: 0.5rem;
    }
    .d-widget-chart-footer {
      display: flex;
      align-items: baseline;
      column-gap: 0.375rem;
      margin-top: 0.5rem;
    }
    .d-widget-chart-aggregate {
      margin-bottom: 0;
      font-size: 0.75rem;
      line-height: 1rem;
      color: var(--cd-ink-2);
    }
    .d-widget-chart-summary {
      align-items: baseline;
      column-gap: 0.375rem;
    }
    .d-widget-chart-total {
      font-weight: 600;
      font-size: 0.9375rem;
      line-height: 1rem;
      font-variant-numeric: tabular-nums;
    }
    .d-widget-chart-information {
      margin-left: 0;
      font-size: 0.75rem;
      line-height: 1rem;
      color: var(--cd-ink-2);
    }
    .d-widget-chart-last-update {
      margin-left: 0.5rem;
      font-size: 0.75rem;
      line-height: 1rem;
      .d-widget-chart-last-update-date {
        margin-left: 0.25rem;
        font-size: 0.75rem;
      }
    }
  }

  /* S9 - list cards become ledgers: the inner card is a row on a hairline */
  .d-widget-list-card {
    .d-widget-list-card__header {
      margin-bottom: 0.25rem;
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
    .d-widget-list-card-title {
      font-weight: 600;
      font-size: 0.8125rem;
      line-height: 1.25rem;
    }
    .d-widget-list-card__footer {
      margin-top: 0.5rem;
      padding-left: 0.75rem;
      padding-right: 0.75rem;
    }
    .d-widget-list-card-wrapper-description {
      margin: 0 0.75rem 0.125rem;
      font-size: 0.75rem;
      line-height: 1.375rem;
      color: var(--cd-ink-2);
    }
    .d-widget-locus-card-wrapper:not(:first-child) {
      margin-top: 0.25rem;
      padding-top: 0.25rem;
    }
    .d-widget-list-card-no-result {
      padding: 0.375rem 0.75rem;
      text-align: left;
      font-size: 0.75rem;
      line-height: 1.25rem;
      color: var(--cd-ink-2);
    }
  }
  .d-widget-locus-card {
    margin: 0;
    padding: 0.5rem 0.75rem;
    border-radius: 0;
    background-color: transparent;
    &:hover {
      background-color: var(--cd-hover);
    }
    & + .d-widget-locus-card {
      border-top: 1px solid var(--cd-rule);
    }
    .d-widget-locus-card-header {
      margin-bottom: 0.125rem;
      .d-widget-locus-card-title {
        font-size: 0.8125rem;
        line-height: 1.125rem;
        -webkit-line-clamp: 1;
      }
    }
    .d-widget-locus-card-content {
      margin-bottom: 0.125rem;
    }
    .d-widget-locus-card-collaboration,
    .d-widget-locus-card-due {
      font-size: 0.75rem;
    }
  }
  /* the literal word "by" carries nothing the avatar and the name do not */
  .d-widget-locus-card-audit-by {
    display: none;
  }
  .d-widget-locus-card-module,
  .d-widget-locus-card-document-id,
  .d-widget-locus-card-audit,
  .d-widget-locus-card-audit-date {
    font-size: 0.75rem;
    color: var(--cd-ink-2);
  }

  /* S10 - state chips: background-image paints over the inline fill, so the hue
     survives as a tint and one neutral ink clears 6:1 on every hue in both themes */
  .fts-state:not(.fts-state--tint) {
    background-image: linear-gradient(var(--cd-wash), var(--cd-wash));
    > div {
      font-size: 0.75rem;
      line-height: 1.125rem;
      color: var(--fts-gray-700);
    }
  }

  /* S11 - the activity stream: events are rows, the comment is a quiet box, and the
     content line that repeats the footer's author and time is hidden. The 50px avatar
     stays: FTSProfile writes its size, and the photo's background-size, inline. */
  .d-widget-activity-stream-card {
    .activity-stream {
      margin: 0;
      padding: 0.5rem 0.75rem;
      border-radius: 0;
      box-shadow: none;
      background: transparent;
      &:first-child {
        margin-top: 0;
      }
      &:hover {
        background-color: var(--cd-hover);
      }
      & + .activity-stream {
        border-top: 1px solid var(--cd-rule);
      }
      .d-widget-locus-card-content {
        display: none;
      }
    }
    .activity-stream-info-header .activity-stream-info-header-title {
      font-size: 0.8125rem;
      line-height: 1.125rem;
    }
    .activity-stream-content {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin-top: 0.25rem;
      padding: 0.375rem 0.5rem;
      border: 1px solid var(--cd-rule);
      border-radius: 0.25rem;
      background-color: var(--cd-hover);
      font-size: 0.8125rem;
      line-height: 1.125rem;
    }
    .activity-stream-card-mode {
      font-size: 0.75rem;
      .activity-stream-card-mode-option--active {
        color: var(--fts-text);
        font-weight: 600;
      }
    }
  }

  /* S12 - the hover cluster loses its box: ghosts on the panel, not a box on a box. It still
     paints, in the panel's own ground - it sits over the title, and a see-through cluster left
     its glyphs lying on the title's text (SS06121). The 2px of padding is that mask's margin;
     top/right give it back, so the icons have not moved. */
  .fts-widget-action {
    top: 0.25rem;
    right: 0.25rem;
    padding: 0.125rem;
    box-shadow: none;
    background-color: inherit;
    > *:not(:last-child) {
      margin-right: 0.125rem;
    }
    .fts-btn.fts-btn-v2.fts-btn-icon {
      width: 1.5rem;
      min-width: 1.5rem;
      max-width: 1.5rem;
      height: 1.5rem;
      max-height: 1.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      color: var(--fts-default);
      &:hover {
        background-color: var(--fts-gray-100);
        color: var(--fts-text);
      }
    }
  }

  /* S13 - the design grip keeps its place in the 12px gutter (an absolutely positioned
     box cannot join the header line without indenting every admin's title) but loses
     the shadowed box: a ghost that inks on hover, and nothing to stack on the panel. */
  .fts-dashboard-widget .dashboard-widget-wrapper.dashboard-widget-wrapper--design .dashboard-widget-action-container {
    top: 0.5rem;
    padding: 0;
    box-shadow: none;
    background-color: transparent;
    color: var(--fts-gray-300);
    &:hover {
      color: var(--fts-text);
    }
  }
}
</style>